'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import { tv } from 'tailwind-variants';
import { faChevronDown, faChevronLeft, faChevronRight } from '@icons/classic/regular';
import { Button } from './Button';
import type { DayButton } from 'react-day-picker';
const calendar = tv({
  base: `
    bg-background
    group/calendar
    p-3
    w-full
    [--cell-size:theme(spacing.8)]
    [[data-slot=card-content]_&]:bg-transparent
    [[data-slot=popover-content]_&]:bg-transparent
  `
});
const calendarRoot = tv({
  base: `
    w-fit
    max-w-full
  `
});
const calendarMonths = tv({
  base: `
    flex
    gap-4
    flex-col
    md:flex-row
    relative
  `
});
const calendarMonth = tv({
  base: `
    flex
    flex-col
    w-full
    gap-4
  `
});
const calendarNav = tv({
  base: `
    flex
    items-center
    gap-1
    w-full
    absolute
    top-0
    inset-x-0
    justify-between
  `,
  variants: {
    layout: {
      dropdown: `
        top-1.5
      `,
      label: ``,
      'dropdown-months': `
        top-1.5
      `,
      'dropdown-years': `
        top-1.5
      `
    }
  }
});
const calendarNavButton = tv({
  base: `
    size-[--cell-size]
    aria-disabled:opacity-50
    p-0
    select-none
  `
});
const calendarMonthCaption = tv({
  base: `
    flex
    items-center
    justify-center
    h-[--cell-size]
    w-full
    px-[--cell-size]
  `
});
const calendarDropdowns = tv({
  base: `
    w-full
    flex
    items-center
    text-sm
    font-medium
    justify-center
    h-[--cell-size]
    gap-1.5
  `
});
const calendarDropdownRoot = tv({
  base: `
    relative
    has-focus:border-ring
    border
    border-input
    shadow-xs
    has-focus:ring-ring/50
    has-focus:ring-[3px]
    dark:hover:bg-input/30
    rounded-md
    `
});
const calendarDropdown = tv({
  base: `
    absolute
    inset-0
    opacity-0
    cursor-pointer
  `
});
const calendarCaptionLabel = tv({
  base: `
    select-none
    font-medium
  `,
  variants: {
    layout: {
      label: `text-sm`,
      dropdown: `
        rounded-md
        pl-2
        pr-1
        flex
        items-center
        gap-1
        text-sm
        h-8
        [&>svg]:text-muted-foreground
        [&>svg]:size-3.5
      `
    }
  }
});
const calendarTable = tv({
  base: `
    w-full
    border-collapse
  `
});
const calendarWeekdays = tv({
  base: `
    flex
  `
});
const calendarWeekday = tv({
  base: `
    text-muted-foreground
    rounded-md
    flex-1
    font-normal
    text-[0.8rem]
    select-none
  `
});
const calendarWeek = tv({
  base: `
    flex
    w-full
    py-1
    gap-2
  `
});
const calendarWeekNumberHeader = tv({
  base: `
    select-none
    w-[--cell-size]
  `
});
const calendarWeekNumber = tv({
  base: `
    text-[0.8rem]
    select-none
    text-muted-foreground
  `
});
const calendarDay = tv({
  base: `
    relative
    w-full
    h-full
    p-0
    text-center
    [&:first-child[data-selected=true]_button]:rounded-l-md
    [&:last-child[data-selected=true]_button]:rounded-r-md
    group/day
    aspect-square
    select-none
  `
});
const calendarRangeStart = tv({
  base: `
    rounded-l-md
  `
});
const calendarRangeMiddle = tv({
  base: `
    rounded-md
  `
});
const calendarRangeEnd = tv({
  base: `
    rounded-r-md
  `
});
const calendarToday = tv({
  base: `
    bg-accent
    text-accent-foreground
    rounded-md
  `
});
const calendarOutside = tv({
  base: `
    text-muted-foreground
    aria-selected:text-muted-foreground
  `
});
const calendarDisabled = tv({
  base: `
    text-muted-foreground
    opacity-50
  `
});
const calendarHidden = tv({
  base: `
    invisible
  `
});
const calendarDayButton = tv({
  base: `
    data-[selected-single=true]:bg-primary
    data-[selected-single=true]:text-primary-foreground
    data-[range-middle=true]:bg-background-tertiary
    data-[range-middle=true]:text-primary
    data-[range-start=true]:bg-primary
    data-[range-start=true]:text-primary-foreground
    data-[range-end=true]:bg-primary
    data-[range-end=true]:text-primary-foreground
    group-data-[focused=true]/day:border-ring
    group-data-[focused=true]/day:ring-ring/50
    flex
    aspect-square
    size-auto
    w-full
    min-w-[--cell-size]
    flex-col
    gap-1
    leading-none
    font-normal
    group-data-[focused=true]/day:relative
    group-data-[focused=true]/day:z-10
    group-data-[focused=true]/day:ring-[3px]
    [&>span]:text-xs
  `
});
function Calendar(t0) {
  const $ = _c(80);
  let className;
  let classNames;
  let components;
  let formatters;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      classNames,
      showOutsideDays: t1,
      captionLayout: t2,
      formatters,
      components,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = classNames;
    $[3] = components;
    $[4] = formatters;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    className = $[1];
    classNames = $[2];
    components = $[3];
    formatters = $[4];
    props = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const showOutsideDays = t1 === undefined ? true : t1;
  const captionLayout = t2 === undefined ? "label" : t2;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[8] !== captionLayout || $[9] !== className) {
    const defaultClassNames = getDefaultClassNames();
    T0 = DayPicker;
    if ($[37] !== className) {
      t28 = calendar({
        className
      });
      $[37] = className;
      $[38] = t28;
    } else {
      t28 = $[38];
    }
    t3 = calendarRoot({
      className: defaultClassNames.root
    });
    t4 = calendarMonths({
      className: defaultClassNames.months
    });
    t5 = calendarMonth({
      className: defaultClassNames.month
    });
    t6 = calendarNav({
      className: defaultClassNames.nav,
      layout: captionLayout
    });
    t7 = calendarNavButton({
      className: defaultClassNames.button_previous
    });
    t8 = calendarNavButton({
      className: defaultClassNames.button_next
    });
    t9 = calendarMonthCaption({
      className: defaultClassNames.month_caption
    });
    t10 = calendarDropdowns({
      className: defaultClassNames.dropdowns
    });
    t11 = calendarDropdownRoot({
      className: defaultClassNames.dropdown_root
    });
    t12 = calendarDropdown({
      className: defaultClassNames.dropdown
    });
    t13 = calendarCaptionLabel({
      layout: captionLayout === "label" ? "label" : "dropdown",
      className: defaultClassNames.caption_label
    });
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = calendarTable();
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    t15 = calendarWeekdays({
      className: defaultClassNames.weekdays
    });
    t16 = calendarWeekday({
      className: defaultClassNames.weekday
    });
    t17 = calendarWeek({
      className: defaultClassNames.week
    });
    t18 = calendarWeekNumberHeader({
      className: defaultClassNames.week_number_header
    });
    t19 = calendarWeekNumber({
      className: defaultClassNames.week_number
    });
    t20 = calendarDay({
      className: defaultClassNames.day
    });
    t21 = calendarRangeStart({
      className: defaultClassNames.range_start
    });
    t22 = calendarRangeMiddle({
      className: defaultClassNames.range_middle
    });
    t23 = calendarRangeEnd({
      className: defaultClassNames.range_end
    });
    t24 = calendarToday({
      className: defaultClassNames.today
    });
    t25 = calendarOutside({
      className: defaultClassNames.outside
    });
    t26 = calendarDisabled({
      className: defaultClassNames.disabled
    });
    t27 = calendarHidden({
      className: defaultClassNames.hidden
    });
    $[8] = captionLayout;
    $[9] = className;
    $[10] = T0;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t13;
    $[15] = t14;
    $[16] = t15;
    $[17] = t16;
    $[18] = t17;
    $[19] = t18;
    $[20] = t19;
    $[21] = t20;
    $[22] = t21;
    $[23] = t22;
    $[24] = t23;
    $[25] = t24;
    $[26] = t25;
    $[27] = t26;
    $[28] = t27;
    $[29] = t28;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
  } else {
    T0 = $[10];
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
    t13 = $[14];
    t14 = $[15];
    t15 = $[16];
    t16 = $[17];
    t17 = $[18];
    t18 = $[19];
    t19 = $[20];
    t20 = $[21];
    t21 = $[22];
    t22 = $[23];
    t23 = $[24];
    t24 = $[25];
    t25 = $[26];
    t26 = $[27];
    t27 = $[28];
    t28 = $[29];
    t3 = $[30];
    t4 = $[31];
    t5 = $[32];
    t6 = $[33];
    t7 = $[34];
    t8 = $[35];
    t9 = $[36];
  }
  let t29;
  if ($[40] !== classNames || $[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t13 || $[45] !== t14 || $[46] !== t15 || $[47] !== t16 || $[48] !== t17 || $[49] !== t18 || $[50] !== t19 || $[51] !== t20 || $[52] !== t21 || $[53] !== t22 || $[54] !== t23 || $[55] !== t24 || $[56] !== t25 || $[57] !== t26 || $[58] !== t27 || $[59] !== t3 || $[60] !== t4 || $[61] !== t5 || $[62] !== t6 || $[63] !== t7 || $[64] !== t8 || $[65] !== t9) {
    t29 = {
      root: t3,
      months: t4,
      month: t5,
      nav: t6,
      button_previous: t7,
      button_next: t8,
      month_caption: t9,
      dropdowns: t10,
      dropdown_root: t11,
      dropdown: t12,
      caption_label: t13,
      month_grid: t14,
      weekdays: t15,
      weekday: t16,
      week: t17,
      week_number_header: t18,
      week_number: t19,
      day: t20,
      range_start: t21,
      range_middle: t22,
      range_end: t23,
      today: t24,
      outside: t25,
      disabled: t26,
      hidden: t27,
      ...classNames
    };
    $[40] = classNames;
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t15;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
    $[55] = t24;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
    $[59] = t3;
    $[60] = t4;
    $[61] = t5;
    $[62] = t6;
    $[63] = t7;
    $[64] = t8;
    $[65] = t9;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== components) {
    t30 = {
      Root: _temp,
      Chevron: _temp2,
      DayButton: CalendarDayButton,
      WeekNumber: _temp3,
      ...components
    };
    $[67] = components;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== formatters) {
    t31 = {
      formatMonthDropdown: _temp4,
      ...formatters
    };
    $[69] = formatters;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== T0 || $[72] !== captionLayout || $[73] !== props || $[74] !== showOutsideDays || $[75] !== t28 || $[76] !== t29 || $[77] !== t30 || $[78] !== t31) {
    t32 = <T0 className={t28} classNames={t29} captionLayout={captionLayout} components={t30} formatters={t31} showOutsideDays={showOutsideDays} {...props} />;
    $[71] = T0;
    $[72] = captionLayout;
    $[73] = props;
    $[74] = showOutsideDays;
    $[75] = t28;
    $[76] = t29;
    $[77] = t30;
    $[78] = t31;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  return t32;
}
function _temp4(date) {
  return date.toLocaleString("default", {
    month: "short"
  });
}
function _temp3(t0) {
  const {
    children,
    ...props_1
  } = t0;
  return <td {...props_1}><div className="flex size-[--cell-size] items-center justify-center text-center">{children}</div></td>;
}
function _temp2(t0) {
  const {
    className: className_1,
    orientation
  } = t0;
  const iconClass = `size-4 ${className_1 || ""}`;
  if (orientation === "left") {
    return <FontAwesomeIcon className={iconClass} icon={faChevronLeft} />;
  }
  if (orientation === "right") {
    return <FontAwesomeIcon className={iconClass} icon={faChevronRight} />;
  }
  return <FontAwesomeIcon className={iconClass} icon={faChevronDown} />;
}
function _temp(t0) {
  const {
    className: className_0,
    rootRef,
    ...props_0
  } = t0;
  return <div className={className_0} data-slot="calendar" ref={rootRef} {...props_0} />;
}
function CalendarDayButton(t0) {
  const $ = _c(21);
  let className;
  let day;
  let modifiers;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      day,
      modifiers,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = day;
    $[3] = modifiers;
    $[4] = props;
  } else {
    className = $[1];
    day = $[2];
    modifiers = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getDefaultClassNames();
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const defaultClassNames = t1;
  const ref = useRef(null);
  let t2;
  let t3;
  if ($[6] !== modifiers.focused) {
    t2 = () => {
      if (modifiers.focused) {
        ref.current?.focus();
      }
    };
    t3 = [modifiers.focused];
    $[6] = modifiers.focused;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  const t4 = `${defaultClassNames.day || ""} ${className || ""}`;
  let t5;
  if ($[9] !== t4) {
    t5 = calendarDayButton({
      className: t4
    });
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== day.date) {
    t6 = day.date.toLocaleDateString();
    $[11] = day.date;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle;
  let t8;
  if ($[13] !== modifiers.range_end || $[14] !== modifiers.range_middle || $[15] !== modifiers.range_start || $[16] !== props || $[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <Button className={t5} data-day={t6} data-range-end={modifiers.range_end} data-range-middle={modifiers.range_middle} data-range-start={modifiers.range_start} data-selected-single={t7} ref={ref} size="sm" variant="secondary" {...props} />;
    $[13] = modifiers.range_end;
    $[14] = modifiers.range_middle;
    $[15] = modifiers.range_start;
    $[16] = props;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
export { Calendar, CalendarDayButton };